import styled from "styled-components";
import React, { useEffect, useRef, useState } from "react";

import {
  setToastState,
  setUpdatedInvoice,
  TInvoice,
  TNexaasProduct,
} from "../../../../redux/reducers/globalState";

import { TButtonIconVariants, TButtonProps } from "../../Button";
import Spinner from "../../Spinner";
import { UilCheckCircle, UilTimesCircle } from "@iconscout/react-unicons";

import {
  backendErrorMessageHandler,
  getQuerystringParams,
} from "../../../../utils/miscellaneous";
import axios, { CancelTokenSource } from "axios";
import apiService from "../../../../services/api";
import { TMockResponse } from "../../../../utils/mocks";
import { format } from "date-fns/format";
import { parseISO } from "date-fns";
import { useTypedDispatch } from "../../../../hooks/useTypedDispatch";

const Container = styled.form``;

const HoldingStepContainer = styled.div`
  display: flex;
  margin-bottom: 12px;

  p {
    font-family: var(--font-paragraph-default);
    font-size: 16px;
    color: var(--color-text-paragraph);
  }

  svg {
    margin-right: 8px;
  }
`;

const IconAndBlockStepContainer = styled.div`
  display: flex;
  align-items: center;

  margin-bottom: 12px;

  svg {
    margin-right: 8px;
  }

  p.subtitle1,
  a,
  .triggerModal {
    font-family: var(--font-paragraph-default);
  }

  p.subtitle1 {
    font-size: 16px;
    color: var(--color-text-paragraph);
  }

  a,
  .triggerModal {
    font-size: inherit;
    color: var(--color-main-blue);
    cursor: pointer;
  }

  .triggerModal {
    padding: 0;

    &:hover {
      background: none;
    }
  }

  p.subtitle2 {
    color: var(--color-text-labels);
    font-size: 12px;
    font-family: var(--font-paragraph-default);
  }

  span {
    color: var(--color-main-rose);
    font-family: var(--font-paragraph-bold);
    font-size: 12px;
  }

  .success-icon {
    color: var(--color-aux-success-700);
  }

  .error-icon {
    color: var(--color-aux-danger-700);
  }
`;

export type TPayableObjectForm = {
  amount: string;
  category_id: number | null;
  classification_center_id: number | null;
  description: string;
  due_date: string;
  mode: null | "in_cash" | "recurrent" | "installments";
  supplier_id: number | null;

  paid_amount?: string;
  fine_amount?: string;
  interest_amount?: string;
  discount_amount?: string;
  payment_date?: string;
  period?:
    | "weekly"
    | "biweekly"
    | "monthly"
    | "bimonthly"
    | "quarterly"
    | "semiannual"
    | "annual";
  installments?: number;
  income_tax_relevant?: boolean;
};

type TGoodsProps = {
  invoice: TInvoice;
  onCheckStockNeeded?: (data: {
    success: TNexaasProduct[];
    error: TNexaasProduct[];
  }) => void;
  footer: { buttons: TButtonProps<TButtonIconVariants>[] };
};

const GoodsAutomated: React.FC<TGoodsProps> = ({
  invoice,
  footer,
  onCheckStockNeeded,
}) => {
  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const intervalId = useRef<NodeJS.Timeout | null>(null);

  const dispatch = useTypedDispatch();

  const [currentStep, setCurrentStep] = useState<
    | "creating"
    | "created"
    | "checking"
    | "failed"
    | "succeeded"
    | "entering"
    | "entered"
  >("created");

  const [steps, setSteps] = useState<{ step: String; element: JSX.Element }[]>(
    []
  );

  const [enteredStockTimestamp, setEnteredStockTimestamp] = useState("");

  const updatedInvoiceRef = useRef(invoice);

  const failedRef = useRef<TNexaasProduct[]>([]);

  const succeededRef = useRef<TNexaasProduct[]>([]);

  const checkingStep = {
    step: "checking",
    element: (
      <HoldingStepContainer>
        <Spinner />
        <p>Realizando conferência de produtos</p>
      </HoldingStepContainer>
    ),
  };

  const createdStep = {
    step: "created",
    element: (
      <IconAndBlockStepContainer>
        <UilCheckCircle className="success-icon" />
        <div>
          <p className="subtitle1">
            Entrada criada com sucesso!&nbsp;
            <a
              target="_blank"
              href={updatedInvoiceRef.current?.goods_receipt_url ?? ""}
            >
              Clique aqui para acessar no OMS
            </a>
          </p>
          {/* <p className="subtitle2">
            <span>Novo</span>&nbsp; Fornecedor:&nbsp;
            {updatedInvoiceRef.current.issuer.name}
          </p> */}
        </div>
      </IconAndBlockStepContainer>
    ),
  };

  const failedCheckStep = {
    step: "failed",
    element: (
      <IconAndBlockStepContainer>
        <UilTimesCircle className="error-icon" />
        <div>
          <p className="subtitle1">Erros encontrados na conferência</p>
          <p className="subtitle2">
            <button
              className="triggerModal"
              role="button"
              onClick={() => {
                if (footer.buttons && footer.buttons[0]?.onClick)
                  footer.buttons[0].onClick({
                    data: {
                      success: succeededRef.current,
                      error: failedRef.current,
                    },
                    shouldUpdate: true,
                  });
              }}
            >
              Visualizar conferência
            </button>
          </p>
        </div>
      </IconAndBlockStepContainer>
    ),
  };

  const succeededCheckStep = {
    step: "succeeded",
    element: (
      <IconAndBlockStepContainer>
        <UilCheckCircle className="success-icon" />
        <div>
          <p className="subtitle1">Conferência realizada</p>
          <p className="subtitle2">
            <button
              className="triggerModal"
              role="button"
              onClick={() => {
                if (footer.buttons && footer.buttons[0]?.onClick)
                  footer.buttons[0].onClick({
                    data: {
                      success: succeededRef.current,
                      error: failedRef.current,
                    },
                    shouldUpdate: true,
                  });
              }}
            >
              Visualizar conferência e lançar estoques
            </button>
          </p>
        </div>
      </IconAndBlockStepContainer>
    ),
  };

  const enteringStockStep = {
    step: "entering",
    element: (
      <IconAndBlockStepContainer>
        <Spinner />
        <div>
          <p className="subtitle1">Lançando estoques</p>
          <p className="subtitle2">Isso pode levar alguns minutos</p>
        </div>
      </IconAndBlockStepContainer>
    ),
  };

  const enteredStockStep = {
    step: "entered",
    element: (
      <React.Fragment key={enteredStockTimestamp}>
        <IconAndBlockStepContainer>
          <UilCheckCircle className="success-icon" />
          <div>
            <p className="subtitle1">Estoques lançados</p>
            {updatedInvoiceRef.current.nexaas_finished_at && (
              <p className="subtitle2">{enteredStockTimestamp}</p>
            )}
          </div>
        </IconAndBlockStepContainer>
      </React.Fragment>
    ),
  };

  const fetchData = async () => {
    const querystrings = getQuerystringParams();

    if (querystrings.organization_id && invoice && invoice.uuid) {
      cancelTokenSource.current = axios.CancelToken.source();

      apiService
        .getInvoice(
          invoice.uuid,
          cancelTokenSource.current.token /* {
          type: "resolve",
          nexaasState: mockNexaasStateRef.current,
        } */
        )
        .then((response: any) => {
          if (response) {
            updatedInvoiceRef.current = response.data;

            dispatch(setUpdatedInvoice(response.data));

            handleNexaasState(updatedInvoiceRef.current);
          }
        });

      if (invoice.nexaas_state < 50) {
        handleNexaasState(invoice);
      }
    }
  };

  const handleNexaasState = (data: TInvoice) => {
    switch (data.nexaas_state) {
      case 20:
        window.location.reload();
        break;
      case 10:
        handleInitialAnimations();
        break;
      case 30:
      case 40:
        setSteps([createdStep, checkingStep]);
        handleSetStep("checking");
        break;
      case 50:
        if (data.nexaas_finished_at) {
          setTimeout(() => {
            setSteps([createdStep, succeededCheckStep, enteredStockStep]);

            handleSetStep("entered");
          }, 100);

          setEnteredStockTimestamp(
            format(parseISO(data.nexaas_finished_at as string), "dd/MM/yyyy") +
              " - " +
              format(parseISO(data.nexaas_finished_at as string), "HH:mm")
          );
        } else {
          setSteps([createdStep, succeededCheckStep, enteringStockStep]);
          handleSetStep("entering");
        }
        break;
      case 60:
        if (intervalId.current && currentStep === "entered") {
          clearInterval(intervalId.current);
        }

        setTimeout(() => {
          setSteps([createdStep, succeededCheckStep, enteredStockStep]);

          handleSetStep("entered");
        }, 100);

        if (data.nexaas_finished_at) {
          setEnteredStockTimestamp(
            format(parseISO(data.nexaas_finished_at as string), "dd/MM/yyyy") +
              " - " +
              format(parseISO(data.nexaas_finished_at as string), "HH:mm")
          );
        }
        break;
    }
  };

  const handleInitialAnimations = () => {
    intervalId.current = setInterval(() => {
      setCurrentStep((before) => {
        if (before === "creating") return "created";
        if (before === "created") return "checking";
        return "checking";
      });
    }, 1500);
  };

  const handlePopulateGoodsStockCheckData = async () => {
    const queryStrings = getQuerystringParams();

    if (!cancelTokenSource.current?.token) {
      cancelTokenSource.current = axios.CancelToken.source();
    }

    // Always fetch latest invoice before deciding
    await apiService
      .getInvoice(invoice.uuid, cancelTokenSource.current.token)
      .then((response: any) => {
        if (response) {
          updatedInvoiceRef.current = response.data;
        } else {
          console.error(
            "No response data received from getInvoice, response: ",
            response
          );
        }
      });

    const maybeCallOnCheckStockNeeded = () => {
      if (
        onCheckStockNeeded &&
        [30, 40].includes(updatedInvoiceRef.current.nexaas_state)
      ) {
        onCheckStockNeeded({
          success: succeededRef.current,
          error: failedRef.current,
        });
      }
    };

    const handleResponse = (value: any) => {
      clearInterval(intervalId.current as NodeJS.Timeout);
      const response = value.data as TMockResponse;
      failedRef.current = response.errors;
      succeededRef.current = response.success;
      maybeCallOnCheckStockNeeded();
      return response;
    };

    const handleResponse1 = (value: any) => {
      clearInterval(intervalId.current as NodeJS.Timeout);
      const response = value.data as TMockResponse;
      failedRef.current = response.errors;
      succeededRef.current = response.success;
      maybeCallOnCheckStockNeeded();
      return response;
    };

    if (cancelTokenSource.current) {
      return apiService
        .getNonCachedGoodsExpressModeStatus(
          queryStrings.organization_id,
          invoice.uuid,
          cancelTokenSource.current.token
        )
        .then(handleResponse1)
        .catch((e) => {
          // Error handling with toast and globalErrors
          clearInterval(intervalId.current as NodeJS.Timeout);

          const globalErrors: string[] = [];
          const errorMessage = backendErrorMessageHandler(e);

          if (typeof errorMessage === "string") {
            globalErrors.push(errorMessage);
          }

          // Retry endpoint on 400
          if (cancelTokenSource.current && e.status === 400) {
            return apiService
              .getNonCachedGoodsExpressModeStatus(
                queryStrings.organization_id,
                invoice.uuid,
                cancelTokenSource.current.token
              )
              .then(handleResponse)
              .catch((err) => {
                clearInterval(intervalId.current as NodeJS.Timeout);
                const errorMessage2 = backendErrorMessageHandler(err);
                if (typeof errorMessage2 === "string") {
                  globalErrors.push(errorMessage2);
                }
                dispatch(
                  setToastState({
                    variant: "error",
                    shouldShow: true,
                    message: {
                      title: "Erro",
                      description: globalErrors.join("\n"),
                    },
                  })
                );
              });
          } else {
            dispatch(
              setToastState({
                variant: "error",
                shouldShow: true,
                message: {
                  title: "Erro",
                  description: globalErrors.join("\n"),
                },
              })
            );
          }
        });
    }
  };

  const fetchVerificationStatus = async () => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }

    // Check if both failed and succeeded product refs are available
    if (failedRef.current && succeededRef.current) {
      // Case 1: No products have been checked yet, but invoice exists
      if (
        failedRef.current.length === 0 &&
        succeededRef.current.length === 0 &&
        invoice &&
        invoice.uuid
      ) {
        // Populate goods stock check data, then determine next step based on results
        handlePopulateGoodsStockCheckData().then(() => {
          // Only proceed if not already in 'entering' or 'entered' step
          if (!["entering", "entered"].includes(currentStep)) {
            // If there are failed products (excluding conversion errors), show failed step
            if (
              Array.isArray(failedRef.current) &&
              failedRef.current.length > 0 &&
              failedRef.current.filter(
                (failed) => !failed.reason?.includes("conversion")
              ).length > 0
            ) {
              setSteps([createdStep, failedCheckStep]);
              handleSetStep("failed");
              if (footer.buttons[0].onClick) {
                footer.buttons[0].onClick({ shouldUpdate: true });
              }
            }
            // If there are succeeded products or only conversion errors, show succeeded step
            else if (
              (Array.isArray(succeededRef.current) &&
                succeededRef.current.length > 0) ||
              (Array.isArray(failedRef.current) &&
                failedRef.current.length > 0 &&
                failedRef.current.filter((failed) =>
                  failed.reason?.includes("conversion")
                ).length > 0)
            ) {
              setSteps([createdStep, succeededCheckStep]);
              handleSetStep("succeeded");
              if (footer.buttons[0].onClick) {
                footer.buttons[0].onClick({ shouldUpdate: true });
              }
            }
          } else {
            // If already entering/entered, just fetch latest data
            fetchData();
          }
        });
      } else {
        // Case 2: There are already checked products (failed or succeeded)
        if (!["entering", "entered"].includes(currentStep)) {
          // If there are failed products (excluding conversion errors), show failed step and trigger update
          if (
            Array.isArray(failedRef.current) &&
            failedRef.current.length > 0 &&
            failedRef.current.filter(
              (failed) => !failed.reason?.includes("conversion")
            ).length > 0
          ) {
            if (footer.buttons[0].onClick) {
              footer.buttons[0].onClick({ shouldUpdate: true });
            }
            setSteps([createdStep, failedCheckStep]);
            handleSetStep("failed");
            return;
          }
          // If there are succeeded products or only conversion errors, show succeeded step and trigger update
          else if (
            (Array.isArray(succeededRef.current) &&
              succeededRef.current.length > 0) ||
            (Array.isArray(failedRef.current) &&
              failedRef.current.length > 0 &&
              failedRef.current.filter((failed) =>
                failed.reason?.includes("conversion")
              ).length > 0)
          ) {
            if (footer.buttons[0].onClick) {
              footer.buttons[0].onClick({ shouldUpdate: true });
            }
            setSteps([createdStep, succeededCheckStep]);
            handleSetStep("succeeded");
            return;
          }
        } else {
          // If already entering/entered, just fetch latest data
          fetchData();
        }
        if (currentStep === "entered") {
          // No-op for 'entered' step
        }
      }
    }
  };

  const handleSetStep = (
    value:
      | "creating"
      | "created"
      | "checking"
      | "failed"
      | "succeeded"
      | "entering"
      | "entered"
  ) => {
    if (value !== currentStep) {
      setCurrentStep(value);
    }
  };

  useEffect(() => {
    fetchData();

    return () => {
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel("from automated, canceling");
      }

      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchVerificationStatus();
  }, [currentStep]);

  useEffect(() => {
    if (invoice?.nexaas_state === 60) {
      handlePopulateGoodsStockCheckData().then(() => {
        if (onCheckStockNeeded) {
          onCheckStockNeeded({
            success: succeededRef.current,
            error: failedRef.current,
          });
        }
      });
    }
  }, [invoice?.nexaas_state]);

  return (
    <Container>
      {invoice.nexaas_state === 60 ? (
        <HoldingStepContainer>
          <Spinner />
          <p>Carregando dados da entrada</p>
        </HoldingStepContainer>
      ) : (
        steps.map((step, index) => (
          <React.Fragment key={index}>{step.element}</React.Fragment>
        ))
      )}
    </Container>
  );
};

export default GoodsAutomated;
